import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { AppSelectComponent } from "./Common/app-select/app-select.component";
import { LoginComponent } from "./Common/login/login.component";
import { UnauthorizedComponent } from "./Common/unauthorized/unauthorized.component";
import { MobileGuard } from "./Mobile/mobile.guard";
import { AuthGuard } from "@auth0/auth0-angular";
import { RoleGuard } from "./Common/guards/role.guard";
import { Constants } from "../constants";
import { ArcusLayoutComponent } from "./Common/arcus-layout/arcus-layout.component";
import { AnonymousLayoutComponent } from "./Common/anonymous-layout/anonymous-layout.component";
import { anonymousGuard } from "./Common/guards/anonymous.guard";
import { softAuthGuard } from "./Common/guards/soft-auth.guard";
import { featureFlagMatch } from "./Common/guards/feature-flag.guard";
import { userPreferenceResolver } from "./SettingsV2/services/user-preference.resolver";

// Duplicate Route notes:
// In order to re-use the layouts (auth layout or anonymous layout) some routes are duplicated
// Authenticated Users: ArcusLayout (navigation, search, etc.)
// Unauthenticated Users: AnonymousLayout (minimal)
// Carrier module, Quickpoint module, and CarrierAutoConfirmation module are shared between both parent layout routes
// An authenticated user on those pages will see the standard toolbar and layout, and have their normal options
// An unauthenticated user on those pages will see a minimal toolbar
// Unauthorized route is duplicated in case we use it in the future for an anonymous page. (Currently only needed or used by the authenticated pages)

const routes: Routes = [
    {
        path: "",
        component: ArcusLayoutComponent,
        canMatch: [softAuthGuard],
        children: [
            { path: "", redirectTo: "home", pathMatch: "full" },
            { path: "unauthorized", title: "Unauthorized", component: UnauthorizedComponent },
            { path: "home", title: "Arcus", component: AppSelectComponent, canActivate: [AuthGuard, MobileGuard] },
            {
                path: "admin",
                loadChildren: () => import("./Admin/admin.module").then((mod) => mod.AdminModule),
                canLoad: [AuthGuard, MobileGuard, RoleGuard],
                canActivate: [AuthGuard, MobileGuard],
                data: { roles: [Constants.roles.SysAdmin, Constants.roles.BranchAdmin] },
            },
            {
                path: "loadboard",
                loadChildren: () => import("./Loadboard/loadboard.routes").then((m) => m.LOADBOARD_ROUTES),
                canLoad: [AuthGuard],
                canActivate: [AuthGuard],
            },
            {
                path: "datamaintenance",
                loadChildren: () =>
                    import("./DataMaintenance/datamaintenance.module").then((mod) => mod.DataMaintenanceModule),
                canLoad: [AuthGuard, MobileGuard],
                canActivate: [AuthGuard, MobileGuard],
            },
            {
                path: "dispatch",
                loadChildren: () => import("./Dispatch/dispatch.module").then((mod) => mod.DispatchModule),
                canLoad: [AuthGuard],
                canActivate: [AuthGuard],
                canActivateChild: [MobileGuard],
            },
            {
                path: "edi",
                loadChildren: () => import("./Edi/edi.module").then((mod) => mod.EdiModule),
                canLoad: [AuthGuard, MobileGuard, RoleGuard],
                canActivate: [AuthGuard, MobileGuard],
                data: { roles: Object.values(Constants.roles.EdiRoles) },
            },
            {
                path: "reporting",
                loadChildren: () => import("./Reporting/reporting.routes").then((m) => m.REPORTING_ROUTES),
                canLoad: [AuthGuard, MobileGuard, RoleGuard],
                canActivate: [AuthGuard, MobileGuard, RoleGuard],
                data: { roles: [Constants.roles.Reporting] },
            },
            {
                path: "legacy-reporting",
                loadChildren: () =>
                    import("./ReportingLegacy/reporting-legacy.module").then((mod) => mod.ReportingLegacyModule),
                canLoad: [AuthGuard, MobileGuard, RoleGuard],
                canActivate: [AuthGuard, MobileGuard, RoleGuard],
                data: { roles: ["REPORT49", "REPORTADMIN"] },
            },
            {
                path: "settings",
                title: "Settings",
                loadComponent: () =>
                    import("./SettingsV2/settings-v2/settings-v2.component").then((m) => m.SettingsV2Component),
                canLoad: [AuthGuard],
                canActivate: [AuthGuard],
                canMatch: [featureFlagMatch("settings-v2")],
                resolve: { preferencesLoaded: userPreferenceResolver },
            },
            {
                path: "settings",
                loadChildren: () => import("./Settings/settings.module").then((mod) => mod.SettingsModule),
                canLoad: [AuthGuard],
                canActivate: [AuthGuard],
            },
            {
                path: "carrier",
                loadChildren: () => import("./Carrier/carrier.module").then((mod) => mod.CarrierModule),
            },
            {
                path: "customer",
                loadChildren: () => import("./Customer/customer.routes").then((m) => m.CUSTOMER_ROUTES),
                canLoad: [AuthGuard, RoleGuard],
                canActivate: [AuthGuard, RoleGuard],
                canActivateChild: [MobileGuard],
                data: { roles: [Constants.roles.Customer] },
            },
            {
                path: "mobile",
                loadChildren: () => import("./Mobile/mobile.module").then((mod) => mod.MobileModule),
                canLoad: [AuthGuard, MobileGuard],
                canActivate: [AuthGuard, MobileGuard],
            },
            {
                path: "carrierautoconfirmation",
                loadChildren: () =>
                    import("./CarrierAutoConfirmation/carrier-auto-confirmation.routes").then(
                        (m) => m.CARRIER_AUTO_CONFIRMATION_ROUTES
                    ),
            },
            {
                path: "quickpoint/:loadid/:code",
                loadChildren: () => import("./QuickPoint/quickpoint.routes").then((m) => m.QUICKPOINT_ROUTES),
                canLoad: [],
                canActivate: [],
            },
        ],
    },
    {
        path: "",
        component: AnonymousLayoutComponent,
        canMatch: [],
        children: [
            { path: "login", title: "Arcus Login", component: LoginComponent, canActivate: [anonymousGuard] },
            { path: "unauthorized", title: "Unauthorized", component: UnauthorizedComponent },
            {
                path: "carrier",
                loadChildren: () => import("./Carrier/carrier.module").then((mod) => mod.CarrierModule),
            },
            {
                path: "carrierautoconfirmation",
                loadChildren: () =>
                    import("./CarrierAutoConfirmation/carrier-auto-confirmation.routes").then(
                        (m) => m.CARRIER_AUTO_CONFIRMATION_ROUTES
                    ),
            },
            {
                path: "quickpoint/:loadid/:code",
                loadChildren: () => import("./QuickPoint/quickpoint.routes").then((m) => m.QUICKPOINT_ROUTES),
                canLoad: [],
                canActivate: [],
            },
            { path: "customer", redirectTo: "/login?customer=true", pathMatch: "full" },
            { path: "", redirectTo: "/login?redirected=true", pathMatch: "full" },
        ],
    },
    // Wildcard redirects to root, which routes to either home (authenticated) or login (anonymous)
    { path: "**", redirectTo: "", pathMatch: "full" },
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule],
})
export class AppRoutingModule {}
